"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPhotoVideo, faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, type ButtonProps, Tooltip, Typography, Wrap, cn } from "@tosize/ui";
import type { ReactNode } from "react";
import { useMedia } from "~/components/media/Provider";

export function MediaButtons({ children }: { children?: ReactNode }) {
	const { sections, overviewMessage, onMediaChange } = useMedia();

	return (
		<div className="grid grid-cols-2 gap-2 border-primary border-b px-4 py-2 sm:flex sm:justify-evenly">
			<MediaButton
				title={overviewMessage}
				icon={faPhotoVideo}
				count={sections.flatMap((sections) => sections.items).length}
				onClick={() => onMediaChange({ type: "overview" })}
			/>

			{sections.map((section) => (
				<MediaButton
					title={section.title}
					icon={section.icon}
					count={section.items.length}
					onClick={() => onMediaChange({ type: "detail", id: section.id, index: 0 })}
					className="hidden sm:[&:nth-child(2)]:inline xl:[&:nth-child(3)]:inline"
					key={section.id}
				/>
			))}

			{children}
		</div>
	);
}

interface MediaButtonProps extends Pick<ButtonProps, "className" | "isLoading" | "onClick"> {
	title: string;
	icon: IconProp;
	count?: number;
	tooltip?: string;
}

export function MediaButton(props: MediaButtonProps) {
	const { title, icon, count, tooltip, isLoading, className, ...remainingProps } = props;

	return (
		<Wrap
			if={!!tooltip}
			with={(children) => (
				<Tooltip color="shades-light" content={tooltip}>
					{children}
				</Tooltip>
			)}
		>
			<Button
				variant="link"
				className={cn("text-center leading-tight", className)}
				{...remainingProps}
			>
				<FontAwesomeIcon
					className={cn({ "animate-spin": isLoading }, "mr-2 xs:inline hidden")}
					icon={isLoading ? faSpinnerThird : icon}
					fixedWidth
					size="sm"
				/>

				<span>{title}</span>

				{!!count && (
					<Typography
						variant="paragraph"
						size="text-sm"
						component="span"
						color="text-shades-dark"
						className="ml-2"
					>
						({count})
					</Typography>
				)}
			</Button>
		</Wrap>
	);
}
